<template>
  <BittsModal
    variant="data"
    :hide-footer="true"
    title="Credit Usage"
    @closed="router.push('/billing')"
    class="credit-usage-modal"
  >
    <BittsCallout
      v-if="error"
      type="warning"
      size="x-small"
      title="We couldn't load your credit usage"
      subtitle="Please try again in a few moments."
      class="mb-16"
      data-testid="credit-usage-error"
    />
    <template v-else>
      <BittsCallout
        v-if="isCreditLimitMasked"
        type="info"
        size="x-small"
        title="During beta, credit usage is uncapped."
        subtitle="These numbers are for informational purposes only. Starting September 1st, credit limits based on your plan will be enforced."
        class="mt-24"
        data-testid="credit-usage-beta-notice"
      />
      <UsageMeter
        class="credit-usage-modal__summary py-24 border-b border-neutral-border"
        label="Credits used"
        :used="creditsUsed"
        :limit="creditLimit"
        :percent-text="limitUsedPercent"
        :width="limitUsedWidth"
        :color="meterColor"
        :is-limit-masked="isCreditLimitMasked"
      >
        <template #sub-line>
          <span v-if="creditResetDate" data-testid="credit-usage-reset">
            {{ creditResetLabel
            }}<span v-if="!isCreditLimitMasked" class="font-bold">{{
              creditResetDate
            }}</span>
          </span>
        </template>
      </UsageMeter>

      <h2 class="text-neutral-text-strong text-lg font-bold mb-8 mt-24">
        Client
      </h2>
      <CreditUsageClientTable :data="data" :is-loading="isLoading" />

      <template v-if="hasUsage">
        <div class="flex items-center justify-between mb-8 mt-24">
          <h2 class="text-neutral-text-strong text-lg font-bold">User</h2>
          <BittsInput
            :model-value="userSearch"
            type="search"
            size="small"
            name="credit-usage-user-search"
            placeholder="Search users"
            class="w-[240px] max-w-[320px]"
            data-testid="credit-usage-user-search"
            @update:model-value="(value) => (userSearch = value)"
          />
        </div>
        <CreditUsageUserTable
          :data="data"
          :is-loading="isLoading"
          :search-query="userSearch"
        />
      </template>
    </template>
  </BittsModal>
</template>

<script setup lang="ts">
import { BittsCallout, BittsInput, BittsModal } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import useCreditLimits from '@/composables/credits/useCreditLimits';
import { useSharedCreditUsage } from '@/composables/credits/useCreditUsage';
import { useFeatureFlagStore } from '@/stores';

import CreditUsageClientTable from './CreditUsageClientTable.vue';
import CreditUsageUserTable from './CreditUsageUserTable.vue';
import UsageMeter from './UsageMeter.vue';

useHead({ title: 'Credit Usage - Crossbeam' });

const router = useRouter();

const { hasFeatureFlag } = useFeatureFlagStore();

const { data, isLoading, error } = useSharedCreditUsage();

const {
  creditsUsed,
  creditLimit,
  limitUsedPercent,
  limitUsedWidth,
  creditLimitStatus,
  creditResetDate,
} = useCreditLimits(data);

const isCreditLimitMasked = computed(
  () => !hasFeatureFlag('temp_mcp_credit_check'),
);

const meterColor = computed(() =>
  isCreditLimitMasked.value ? 'secondary' : creditLimitStatus.value,
);

const creditResetLabel = computed(() =>
  isCreditLimitMasked.value
    ? 'Credits reset annually'
    : 'Credits reset annually on ',
);

const userSearch = ref('');

const hasUsage = computed(() =>
  (data.value?.users ?? []).some((user) => (user.credits_used ?? 0) > 0),
);
</script>
